import React from "react";
import PropTypes from "prop-types";

import { Button } from "semantic-ui-react";

import { useFeatures } from "components/FeatureFlag";
import Form from "components/Form";
import Modal from "components/Modal";

import { useProcesses, useShareResourcesForm } from "./hooks";
import { ProcessesField } from "./components";

function ShareResourcesModal({ onClose, onSubmit, process }) {
  const processes = useProcesses();
  const sharedResourcesFlag = useFeatures("shared-resources");

  const { formProps, isReady, submit, isSubmitting } = useShareResourcesForm(
    onSubmit,
    process
  );

  return (
    <Modal
      onClose={onClose}
      isModalOpen
      size="small"
      data-test="share-resources-modal"
    >
      <Modal.Header onClose={onClose}>
        {sharedResourcesFlag?.modal?.title || "Share Resources"}
      </Modal.Header>
      <Modal.Body scrolling>
        <div style={{ marginBottom: "1.7em" }}>
          {(
            sharedResourcesFlag?.modal?.subtitles || [
              "Recipients with whom you share a resource will be able to install it in their hub and will also receive an email " +
                "notification (if they have not received one from you in the last 24 hours). " +
                "Once you share a resource, you cannot revoke access to it.",
              "Recipients can install your shared workflows in their hub and get started with Hubly instantly.",
            ]
          ).map((subtitle) => {
            return <p key={subtitle}>{subtitle}</p>;
          })}

          <p>
            <i>
              {sharedResourcesFlag?.modal?.disclaimer?.text ||
                "Shared resources cannot be deleted from your hub after they are shared."}{" "}
              {sharedResourcesFlag?.modal?.disclaimer?.helpArticle?.url && (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={sharedResourcesFlag.modal.disclaimer.helpArticle.url}
                  data-test="help-article-link"
                >
                  {sharedResourcesFlag?.modal?.disclaimer?.helpArticle?.text ||
                    "Learn More"}
                </a>
              )}
            </i>
          </p>
        </div>

        <Form id="shareResources" onSubmit={submit} {...formProps}>
          <Form.CreatableInput
            required
            name="emails"
            label="Share with"
            data-test="recipient-emails-input"
            placeholder="Enter recipient email addresses"
            addOnSpace
            disabled={
              processes.isEmpty ||
              processes.initialFetch ||
              processes.error ||
              isSubmitting
            }
          />

          <ProcessesField processes={processes} disabled={isSubmitting} />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <span style={{ fontSize: "11px", textAlign: "left" }}>
            By sharing workflows & processes with new users you agree with{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.myhubly.com/referral-program-terms-conditions"
              data-test="referral-program-link"
              className="underline_on_hover"
            >
              Hubly&apos;s Referral Program Terms and Conditions.
            </a>
          </span>

          <div style={{ display: "flex", gap: "14px" }}>
            <Button basic content="Cancel" onClick={onClose} />
            <Button
              primary
              type="submit"
              content="Share"
              form="shareResources"
              disabled={!isReady || isSubmitting}
              loading={isSubmitting}
            />
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

ShareResourcesModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  process: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
};

ShareResourcesModal.defaultProps = {
  onSubmit: () => {},
  process: undefined,
};

export default ShareResourcesModal;
